import { addDays, format, startOfDay } from 'date-fns'
import { AnimatePresence, motion } from 'motion/react'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ProtectiveOrder } from '@pages/Trade/Portfolio/components/ProtectiveOrder/ProtectiveOrder'
import { useDxtradeAccountMetricsWS } from '@pages/Trade/Portfolio/context/DxtradeAccountMetricsContext'
import { useConversionRate } from '@pages/Trade/Portfolio/hooks/useConversionRatesStore'
import { invertSide } from '@pages/Trade/Portfolio/utils/mapping'
import { getDefaultProtectivePrice } from '@pages/Trade/Portfolio/utils/tpslHelpers'
import type { QuantityValidationError } from '@pages/Trade/components/QuantityInput'

import { BaseModalType, useModal } from '@context/ModalsContext'

import { Body1, Caption1 } from '@ui/Typography'
import { AlertNotice } from '@ui/components/AlertNotice'
import { IconButton } from '@ui/components/Button'
import { Divider } from '@ui/components/Divider'
import { Input } from '@ui/components/Input/Input'
import { Modal } from '@ui/components/Modal'
import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'
import { Toggle } from '@ui/components/Toggle/Toggle'
import { Tooltip } from '@ui/components/Tooltip'
import { CalendarIcon } from '@ui/icons/Calendar.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { MinusIcon } from '@ui/icons/Minus.icon'
import { PlusIcon } from '@ui/icons/Plus.icon'
import { Calendar } from '@ui/v2/Calendar/Calendar'
import { Scrollbar } from '@ui/v2/Scrollbar/Scrollbar'

import { useWindowResize } from '@utils/domUtils'

import {
  DxTradeContingencyTypes,
  DxTradeOrderTypes,
  ProtectiveOrderTypes,
} from '@model/DxTrade.types'
import type {
  DxTradeExecutableSide,
  DxTradeInstrumentDetails,
  DxTradeInstrumentDetailsResponseItem,
  DxTradePosition,
  FixOrderGroupPayload,
  FixSingleOrderPayload,
} from '@model/DxTrade.types'
import type { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { FIX_POSITION_EFFECT, FIX_TIME_IN_FORCE } from '../utils/constants'
import {
  clampQuantity,
  computeDisplayProps,
  formatExpireDateUtc,
  formatValueWithStep,
  getPriceIncrementErrorMessage,
  getSlPriceErrorMessage,
  getStepDecimals,
  getTpPriceErrorMessage,
  mapOrderTypeToFixOrdType,
  preventNumericInputChars,
  snapToStep,
} from '../utils/helpers'
import { OrderSizeSection } from './components/OrderSizeSection'
import { calculateMarginFactor, useDynamicLeverage } from './hooks/useDynamicLeverage'
import { useTradingSessionStatus } from './hooks/useTradingSessionStatus'
import { determineOrderType, getMarginRequiredDisplay } from './utils/helpers'

import styles from './Widget.module.scss'

interface OrderWidgetBodyProps {
  side: DxTradeExecutableSide
  setSide: Dispatch<SetStateAction<DxTradeExecutableSide>>
  accountCode: string
  selectedAccountFreeMargin?: number | null
  instrumentSymbol: string
  currentPrice?: number | undefined
  tpslBasePrice?: number | undefined
  accountCurrency?: CurrencyType
  onValidityChange?: (disabled: boolean) => void
  onOrderPayloadChange?: (payload: FixSingleOrderPayload | FixOrderGroupPayload | null) => void
  instrument?: DxTradeInstrumentDetailsResponseItem | null
  instrumentInfo?: DxTradeInstrumentDetails | undefined
  isInstrumentLoading?: boolean
  openPositions?: DxTradePosition[]
}

const priceCondVariants = {
  hidden: { opacity: 0, height: 0, marginTop: -16 },
  visible: { opacity: 1, height: 'auto', marginTop: 0 },
}

const priceCondTransition = { duration: 0.28, ease: 'easeInOut' as const }

export const OrderWidgetBody = (props: OrderWidgetBodyProps) => {
  const {
    side,
    setSide,
    accountCode,
    selectedAccountFreeMargin,
    instrumentSymbol,
    currentPrice,
    tpslBasePrice,
    accountCurrency,
    onValidityChange,
    onOrderPayloadChange,
    instrument,
    instrumentInfo,
    isInstrumentLoading,
    openPositions,
  } = props

  const { t } = useTranslation()
  const addCancellationDateModal = useModal(AddCancellationDateModal)
  const wsMetrics = useDxtradeAccountMetricsWS(`default:${accountCode}`)
  const freeMargin = wsMetrics?.marginFree ?? selectedAccountFreeMargin ?? null

  const isLoading = isInstrumentLoading ?? false

  const minOrderSizeLots = instrument?.minOrderSize ?? null
  const maxOrderSizeLots = instrument?.maxOrderSize?.value ?? null
  const minOrderSizeIncrementLots = instrument?.minOrderSizeIncrement ?? null
  const lotSize = instrumentInfo?.lotSize

  const effectiveStepLots = minOrderSizeIncrementLots ?? minOrderSizeLots

  const convertLotsToUnits = (value: number | null | undefined) => {
    if (value == null) return value ?? null
    if (!lotSize || lotSize === 0) return value
    return value * lotSize
  }

  const minOrderSizeUnits = convertLotsToUnits(minOrderSizeLots)
  const maxOrderSizeUnits = convertLotsToUnits(maxOrderSizeLots)
  const effectiveStepUnits = convertLotsToUnits(effectiveStepLots)

  const [unit, setUnit] = useState<'units' | 'lots'>('lots')
  const [qty, setQty] = useState<number>(minOrderSizeUnits ?? 0)
  const [lastInstrumentSymbol, setLastInstrumentSymbol] = useState(instrumentSymbol)

  const [priceCondEnabled, setPriceCondEnabled] = useState(false)
  const [targetPrice, setTargetPrice] = useState<number | ''>('')
  const [targetPriceInput, setTargetPriceInput] = useState<string>('')
  const [cancelDate, setCancelDate] = useState<Date | null>(null)

  const [tpEnabled, setTpEnabled] = useState(false)
  const [tpPrice, setTpPrice] = useState<number | ''>('')

  const [slEnabled, setSlEnabled] = useState(false)
  const [slPrice, setSlPrice] = useState<number | ''>('')

  const [tpHasInternalError, setTpHasInternalError] = useState(false)
  const [slHasInternalError, setSlHasInternalError] = useState(false)

  const [qtyValidationError, setQtyValidationError] = useState<QuantityValidationError | null>(null)

  const [isVerticalScrollable, setIsVerticalScrollable] = useState(false)

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const hasManualTargetPriceEditRef = useRef(false)
  const prevSideRef = useRef<typeof side | undefined>(undefined)

  const { isOpen } = useTradingSessionStatus(instrumentInfo?.tradingHours)

  const isMobile = useWindowResize()
  const priceStep: number | undefined = instrumentInfo?.priceIncrement ?? undefined

  useEffect(() => {
    setQty(0)
    setLastInstrumentSymbol(instrumentSymbol)
    setSlEnabled(false)
    setSlPrice('')
    setTpEnabled(false)
    setTpPrice('')
    setCancelDate(null)
    setPriceCondEnabled(false)
    setTargetPrice('')
    setTargetPriceInput('')
    hasManualTargetPriceEditRef.current = false
  }, [instrumentSymbol])

  useEffect(() => {
    if (prevSideRef.current === undefined) {
      prevSideRef.current = side
      return
    }
    if (prevSideRef.current === side) return
    prevSideRef.current = side

    let slTpBase: number | null = tpslBasePrice ?? null

    if (priceCondEnabled) {
      const newTarget = typeof currentPrice === 'number' ? currentPrice : null
      if (newTarget !== null) {
        const formatted = formatValueWithStep(newTarget, priceStep)
        const parsed = formatted !== '' ? Number(formatted) : newTarget
        setTargetPrice(parsed)
        setTargetPriceInput(formatted)
      }
      slTpBase = newTarget
    }

    if (slEnabled) {
      const next = getDefaultProtectivePrice({
        type: ProtectiveOrderTypes.stopLoss,
        isActive: true,
        prevPrice: '',
        basePrice: slTpBase,
        pipSize,
        baseSide: side,
      })
      setSlPrice(
        typeof next === 'number' && priceStep ? Number(formatValueWithStep(next, priceStep)) : next
      )
    }

    if (tpEnabled) {
      const next = getDefaultProtectivePrice({
        type: ProtectiveOrderTypes.takeProfit,
        isActive: true,
        prevPrice: '',
        basePrice: slTpBase,
        pipSize,
        baseSide: side,
      })
      setTpPrice(
        typeof next === 'number' && priceStep ? Number(formatValueWithStep(next, priceStep)) : next
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side])

  useEffect(() => {
    if (isLoading || instrumentInfo === undefined) return
    if (isOpen) {
      return
    }

    setPriceCondEnabled(true)

    if (hasManualTargetPriceEditRef.current) {
      return
    }

    if (targetPriceInput !== '') {
      return
    }

    if (typeof currentPrice === 'number') {
      setTargetPrice(currentPrice)
      setTargetPriceInput(formatValueWithStep(currentPrice, priceStep))
    }
  }, [
    isOpen,
    isLoading,
    instrumentInfo,
    instrumentSymbol,
    currentPrice,
    priceStep,
    targetPriceInput,
  ])

  useEffect(() => {
    if (minOrderSizeUnits == null) {
      return
    }
    setQty((prev) => clampQuantity(prev, minOrderSizeUnits, maxOrderSizeUnits))
  }, [minOrderSizeUnits, maxOrderSizeUnits])

  useEffect(() => {
    const container = scrollContainerRef.current

    if (!container) {
      return
    }

    const updateScrollable = () => {
      setIsVerticalScrollable(container.scrollHeight > container.clientHeight + 1)
    }

    updateScrollable()

    const resizeObserver = new ResizeObserver(() => {
      updateScrollable()
    })

    resizeObserver.observe(container)
    window.addEventListener('resize', updateScrollable)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateScrollable)
    }
  }, [slEnabled, tpEnabled, priceCondEnabled, isOpen])

  const entryPrice =
    priceCondEnabled && typeof targetPrice === 'number' ? targetPrice : currentPrice

  const effectiveTpslBase =
    priceCondEnabled && typeof targetPrice === 'number' ? targetPrice : tpslBasePrice ?? null

  const pipSize = instrumentInfo?.pipSize

  const minCancelDate = startOfDay(addDays(new Date(), 1))

  const cappedMaxForInput = maxOrderSizeLots ?? undefined
  const qtyInLots = lotSize && lotSize !== 0 ? qty / lotSize : qty
  const orderExposureDeltaLots =
    instrument && minOrderSizeUnits != null ? (side === 'SELL' ? -qtyInLots : qtyInLots) : undefined

  const instrumentCurrency = instrumentInfo?.currency ?? null

  const { dynamicLeverage } = useDynamicLeverage(instrumentSymbol)
  const hasDynamicLeverage = !!dynamicLeverage
  const { rate: marginConversionRate, requiresConversion: requiresMarginConversion } =
    useConversionRate(instrumentCurrency, accountCurrency)

  const existingLotsForInstrument = useMemo(() => {
    if (!openPositions?.length || !lotSize || lotSize === 0) return 0
    const netUnits = openPositions.reduce(
      (sum, p) => sum + (p.side === 'BUY' ? p.quantity : -p.quantity),
      0
    )
    return netUnits / lotSize
  }, [openPositions, lotSize])

  const marginFactor = useMemo(() => {
    if (instrumentSymbol !== lastInstrumentSymbol) {
      return undefined
    }
    if (!dynamicLeverage || !instrument || minOrderSizeUnits == null) {
      return undefined
    }
    if (
      orderExposureDeltaLots == null ||
      Number.isNaN(orderExposureDeltaLots) ||
      orderExposureDeltaLots === 0
    ) {
      return undefined
    }

    if (!lotSize) {
      return undefined
    }

    return calculateMarginFactor(
      dynamicLeverage.tiers,
      orderExposureDeltaLots,
      lotSize,
      existingLotsForInstrument
    )
  }, [
    instrumentSymbol,
    lastInstrumentSymbol,
    dynamicLeverage,
    instrument,
    minOrderSizeUnits,
    orderExposureDeltaLots,
    lotSize,
    existingLotsForInstrument,
  ])

  const marginRequired = useMemo(() => {
    if (marginFactor == null || !currentPrice || currentPrice <= 0) {
      return undefined
    }

    const rawMargin = marginFactor * currentPrice
    if (requiresMarginConversion) {
      if (marginConversionRate == null) {
        return undefined
      }
      return rawMargin * marginConversionRate
    }

    return rawMargin
  }, [marginFactor, currentPrice, requiresMarginConversion, marginConversionRate])
  const marginRequiredDisplayValue = useMemo(() => {
    if (marginRequired == null) {
      return undefined
    }
    return Math.round(marginRequired * 100) / 100
  }, [marginRequired])

  const marginRequiredDisplay = useMemo(() => {
    return getMarginRequiredDisplay(marginRequiredDisplayValue, freeMargin)
  }, [marginRequiredDisplayValue, freeMargin])

  const hasMarginError = useMemo(() => {
    if (marginRequired == null || marginRequired <= 0 || freeMargin == null) {
      return false
    }
    return marginRequired > freeMargin
  }, [marginRequired, freeMargin])

  const marginRequiredErrorText = hasMarginError
    ? t('OrderWidget.Not enough margin to place the order')
    : undefined

  // Validation for SL/TP prices
  const slPriceErrorMessage = getSlPriceErrorMessage(slPrice, effectiveTpslBase, side, t, priceStep)

  const tpPriceErrorMessage = getTpPriceErrorMessage(tpPrice, effectiveTpslBase, side, t, priceStep)

  const priceIncrementErrorMessage = priceCondEnabled
    ? getPriceIncrementErrorMessage(targetPriceInput, priceStep, t)
    : undefined

  const priceValueErrorMessage =
    priceCondEnabled && targetPriceInput !== '' && Number(targetPriceInput) <= 0
      ? t('OrderWidget.Price must be greater than 0')
      : undefined

  const priceEqualsCurrentErrorMessage =
    priceCondEnabled &&
    typeof targetPrice === 'number' &&
    typeof currentPrice === 'number' &&
    targetPrice === currentPrice
      ? t('OrderWidget.Target price must differ from current price')
      : undefined

  const priceDisplayError =
    priceValueErrorMessage ?? priceIncrementErrorMessage ?? priceEqualsCurrentErrorMessage

  const hasPriceTriggerError =
    priceCondEnabled &&
    (typeof targetPrice !== 'number' ||
      !!priceIncrementErrorMessage ||
      !!priceValueErrorMessage ||
      !!priceEqualsCurrentErrorMessage)
  const hasSlError = slEnabled && !!slPriceErrorMessage
  const hasTpError = tpEnabled && !!tpPriceErrorMessage
  const hasQtyError = qtyValidationError != null
  const isSubmitDisabled =
    hasPriceTriggerError ||
    hasSlError ||
    hasTpError ||
    (slEnabled && slHasInternalError) ||
    (tpEnabled && tpHasInternalError) ||
    hasMarginError ||
    hasQtyError

  const orderType = useMemo(() => {
    return determineOrderType(priceCondEnabled, targetPrice, currentPrice, side)
  }, [priceCondEnabled, targetPrice, currentPrice, side])

  useEffect(() => {
    if (isLoading || !instrument || minOrderSizeUnits == null) return
    onValidityChange?.(isSubmitDisabled)
  }, [isSubmitDisabled, onValidityChange, isLoading, instrument, minOrderSizeUnits])

  useEffect(() => {
    if (!onOrderPayloadChange) return

    if (isLoading || isSubmitDisabled) {
      onOrderPayloadChange(null)
      return
    }

    const qtyDecimals = getStepDecimals(effectiveStepUnits) ?? getStepDecimals(minOrderSizeUnits)
    const safeQty = Number.isFinite(qty) ? Number(qty.toFixed(qtyDecimals)) : qty

    const ordType = mapOrderTypeToFixOrdType(orderType)

    const nextPayload: FixSingleOrderPayload = {
      instrument: instrumentSymbol,
      quantity: safeQty,
      side,
      ordType,
      positionEffect: FIX_POSITION_EFFECT.OPEN,
      timeInForce: cancelDate ? FIX_TIME_IN_FORCE.GOOD_TILL_DATE : FIX_TIME_IN_FORCE.DAY,
      expireDate:
        orderType !== DxTradeOrderTypes.MARKET && cancelDate
          ? formatExpireDateUtc(cancelDate)
          : undefined,
    }

    if (typeof targetPrice === 'number') {
      if (orderType === DxTradeOrderTypes.LIMIT || orderType === DxTradeOrderTypes.STOP) {
        nextPayload.price = targetPrice
      }
    }

    const hasSlOrTp =
      (slEnabled && typeof slPrice === 'number') || (tpEnabled && typeof tpPrice === 'number')

    if (hasSlOrTp) {
      // Create IF-THEN group
      const orders: FixSingleOrderPayload[] = [nextPayload]

      // Add SL order if enabled
      if (slEnabled && typeof slPrice === 'number') {
        const slOrder: FixSingleOrderPayload = {
          instrument: instrumentSymbol,
          quantity: 0,
          side: invertSide(side) as DxTradeExecutableSide,
          ordType: '3',
          price: slPrice,
          positionEffect: FIX_POSITION_EFFECT.CLOSE,
          timeInForce: FIX_TIME_IN_FORCE.DAY,
        }
        orders.push(slOrder)
      }

      // Add TP order if enabled
      if (tpEnabled && typeof tpPrice === 'number') {
        const tpOrder: FixSingleOrderPayload = {
          instrument: instrumentSymbol,
          quantity: 0,
          side: invertSide(side) as DxTradeExecutableSide,
          ordType: '2',
          price: tpPrice,
          positionEffect: FIX_POSITION_EFFECT.CLOSE,
          timeInForce: FIX_TIME_IN_FORCE.DAY,
        }
        orders.push(tpOrder)
      }

      const groupPayload: FixOrderGroupPayload = {
        orders,
        contingencyType: DxTradeContingencyTypes.IF_THEN,
      }

      onOrderPayloadChange(groupPayload)
    } else {
      onOrderPayloadChange(nextPayload)
    }
  }, [
    onOrderPayloadChange,
    isLoading,
    instrumentSymbol,
    qty,
    side,
    targetPrice,
    cancelDate,
    isSubmitDisabled,
    orderType,
    slEnabled,
    tpEnabled,
    slPrice,
    tpPrice,
  ])

  if (isLoading || !instrument || minOrderSizeUnits == null) {
    return (
      <Box px='x16' py='x24' flex align='center' justify='center'>
        <Loading isLoading showLoadingIcon />
      </Box>
    )
  }

  const handleQtyChange = (value: number) => {
    const qtyInUnits = unit === 'lots' && lotSize ? value * lotSize : value

    setQty(qtyInUnits)
  }

  const handleUnitChange = (nextUnit: 'units' | 'lots') => {
    if (nextUnit === unit || !lotSize) return
    setUnit(nextUnit)

    setQty((prevQty) => {
      if (minOrderSizeUnits == null && maxOrderSizeUnits == null && effectiveStepUnits == null) {
        return prevQty
      }

      const snapped = snapToStep(
        prevQty,
        effectiveStepUnits ?? undefined,
        minOrderSizeUnits ?? undefined
      )

      return clampQuantity(snapped, minOrderSizeUnits, maxOrderSizeUnits)
    })
  }

  const handleTargetPriceChange = (raw: string) => {
    hasManualTargetPriceEditRef.current = true
    setTargetPriceInput(raw)

    if (raw === '') {
      setTargetPrice('')
      return
    }

    const num = Number(raw)
    if (!Number.isFinite(num) || num < 0) {
      return
    }

    setTargetPrice(num)
  }

  const handleTargetPriceStepChange = (direction: 1 | -1) => {
    if (!priceStep) return

    hasManualTargetPriceEditRef.current = true

    setTargetPrice((prev) => {
      const baseValue = typeof prev === 'number' ? prev : 0

      const decimals = getStepDecimals(priceStep)

      const nextValue = Number((baseValue + direction * priceStep).toFixed(decimals))

      if (nextValue <= 0) return prev
      setTargetPriceInput(formatValueWithStep(nextValue, priceStep))
      return nextValue
    })
  }

  const handleSlActiveChange = (nextEnabled: boolean) => {
    if (!nextEnabled) {
      setSlEnabled(false)
      return
    }
    setSlEnabled(true)
    setSlPrice(() => {
      const next = getDefaultProtectivePrice({
        type: ProtectiveOrderTypes.stopLoss,
        isActive: true,
        prevPrice: '',
        basePrice: effectiveTpslBase,
        pipSize,
        baseSide: side,
      })
      return typeof next === 'number' && priceStep
        ? Number(formatValueWithStep(next, priceStep))
        : next
    })
  }

  const handleTpActiveChange = (nextEnabled: boolean) => {
    if (!nextEnabled) {
      setTpEnabled(false)
      return
    }
    setTpEnabled(true)
    setTpPrice(() => {
      const next = getDefaultProtectivePrice({
        type: ProtectiveOrderTypes.takeProfit,
        isActive: true,
        prevPrice: '',
        basePrice: effectiveTpslBase,
        pipSize,
        baseSide: side,
      })
      return typeof next === 'number' && priceStep
        ? Number(formatValueWithStep(next, priceStep))
        : next
    })
  }

  const handlePriceConditionToggle = (nextEnabled: boolean) => {
    setPriceCondEnabled(nextEnabled)
    hasManualTargetPriceEditRef.current = false

    if (!nextEnabled) {
      setTargetPrice('')
      setTargetPriceInput('')
      setCancelDate(null)
      return
    }

    if (typeof currentPrice === 'number') {
      setTargetPrice(currentPrice)
      setTargetPriceInput(formatValueWithStep(currentPrice, priceStep))
    } else {
      setTargetPrice('')
      setTargetPriceInput('')
    }
  }

  const {
    qtyDisplay: displayQty,
    minDisplay: displayMinOrderSize,
    maxDisplay: displayMaxOrderSize,
    stepDisplay: displayStep,
  } = computeDisplayProps(
    qtyInLots,
    unit,
    lotSize,
    minOrderSizeLots,
    cappedMaxForInput,
    effectiveStepLots
  )

  const priceCondLabel =
    side === 'BUY' ? t('OrderWidget.Buy when price is') : t('OrderWidget.Sell when price is')

  return (
    <div className={styles.scrollAreaWrapper}>
      <Box
        px={isMobile ? undefined : 'x16'}
        flex
        direction='column'
        gap='x16'
        className={styles.body}
        ref={scrollContainerRef}
      >
        <Box>
          <SegmentedControl
            size='md'
            variant='default'
            activeTab={side}
            tabs={[
              { id: 'BUY', name: t('OrderWidget.Buy'), onClick: () => setSide('BUY') },
              { id: 'SELL', name: t('OrderWidget.Sell'), onClick: () => setSide('SELL') },
            ]}
          />
        </Box>

        <OrderSizeSection
          qty={displayQty}
          onQtyChange={handleQtyChange}
          onQtyValidationError={setQtyValidationError}
          unit={unit}
          onUnitChange={handleUnitChange}
          minOrderSize={displayMinOrderSize}
          maxOrderSize={displayMaxOrderSize}
          minOrderSizeIncrement={displayStep}
          marginValue={hasDynamicLeverage ? marginRequiredDisplay : undefined}
          accountCurrency={accountCurrency}
          marginErrorText={hasDynamicLeverage ? marginRequiredErrorText : undefined}
          showMarginRequired={hasDynamicLeverage}
        />

        <Box flexItem='0 0 auto'>
          <Divider.Horizontal />
        </Box>

        <Box flex direction='column' gap='x16'>
          <Box flex justify='space-between' align='center' gap='x12'>
            <Box flex align='center' gap='x6'>
              <Body1 medium color='text-high-em'>
                {priceCondLabel}
              </Body1>
              <Tooltip
                content={
                  side === 'BUY'
                    ? t('OrderWidget.Buy When Price Is Tooltip')
                    : t('OrderWidget.Sell When Price Is Tooltip')
                }
                direction='top'
                heading={isMobile ? priceCondLabel : undefined}
                secondaryButton={isMobile ? { label: t('Close') } : undefined}
              >
                <InfoIcon size={20} color='text-low-em' />
              </Tooltip>
            </Box>
            <Toggle
              name='order-price-trigger'
              value={isOpen ? priceCondEnabled : true}
              disabled={!isOpen}
              onChange={handlePriceConditionToggle}
            />
          </Box>

          <AnimatePresence>
            {(priceCondEnabled || !isOpen) && (
              <motion.div
                variants={priceCondVariants}
                initial='hidden'
                animate='visible'
                exit='hidden'
                transition={priceCondTransition}
                className={styles.collapseContent}
              >
                <Box flex direction='column' gap='x16'>
                  {!isOpen && (
                    <AlertNotice
                      id={'order-market-closed'}
                      type='info'
                      title={t('OrderWidget.MarketClosed')}
                    />
                  )}
                  <Box flex align='center' gap='x8' fullWidth>
                    <IconButton
                      variant='secondary'
                      size='md'
                      icon={MinusIcon}
                      onClick={() => handleTargetPriceStepChange(-1)}
                    />
                    <Input
                      label={t('OrderWidget.Price')}
                      value={targetPriceInput}
                      onChange={(e) => handleTargetPriceChange(e.currentTarget.value)}
                      onKeyDown={preventNumericInputChars}
                      onWheel={(e) => e.currentTarget.blur()}
                      type='number'
                      inputMode='decimal'
                      step={priceStep}
                      min={0}
                      decimalPlaces={getStepDecimals(priceStep) ?? undefined}
                      placeholder={t('OrderWidget.Price')}
                      showLabel={false}
                      fullWidth
                      size='lg'
                      error={!!priceDisplayError}
                    />
                    <IconButton
                      variant='secondary'
                      size='md'
                      icon={PlusIcon}
                      onClick={() => handleTargetPriceStepChange(1)}
                    />
                  </Box>
                  {priceDisplayError && (
                    <Caption1 color='text-danger-high-em'>{priceDisplayError}</Caption1>
                  )}

                  <Input
                    label={t('OrderWidget.Add cancellation date')}
                    value={cancelDate ? format(cancelDate, 'dd/MM/yyyy') : ''}
                    placeholder={t('OrderWidget.Add cancellation date')}
                    rightIcon={CalendarIcon}
                    showLabel={false}
                    fullWidth
                    size='lg'
                    onClick={() => {
                      addCancellationDateModal.open({
                        cancelDate,
                        minCancelDate,
                        onConfirm: (date: Date) => {
                          setCancelDate(date ?? null)
                        },
                        onClear: () => {
                          setCancelDate(null)
                        },
                      })
                    }}
                  />
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        <Box flexItem='0 0 auto'>
          <Divider.Horizontal />
        </Box>

        {/* Stop Loss */}
        <ProtectiveOrder
          title={t('OrderWidget.Stop Loss')}
          type={ProtectiveOrderTypes.stopLoss}
          price={slPrice}
          isActive={slEnabled}
          onActiveChange={handleSlActiveChange}
          onPriceChange={setSlPrice}
          instrumentMeta={{ pipSize: pipSize ?? null, priceIncrement: priceStep }}
          currentPrice={effectiveTpslBase ?? currentPrice}
          baseSide={side}
          basePrice={effectiveTpslBase}
          mode='new'
          externalQuantity={qty}
          externalOpenPrice={entryPrice ?? null}
          externalSide={side}
          accountCurrency={accountCurrency}
          instrumentCurrency={instrumentInfo?.currency as CurrencyType | undefined}
          onErrorChange={setSlHasInternalError}
        />
        <Box flexItem='0 0 auto'>
          <Divider.Horizontal />
        </Box>

        {/* Take Profit */}
        <ProtectiveOrder
          title={t('OrderWidget.Take Profit')}
          type={ProtectiveOrderTypes.takeProfit}
          price={tpPrice}
          isActive={tpEnabled}
          onActiveChange={handleTpActiveChange}
          onPriceChange={setTpPrice}
          instrumentMeta={{ pipSize: pipSize ?? null, priceIncrement: priceStep }}
          currentPrice={effectiveTpslBase ?? currentPrice}
          baseSide={side}
          basePrice={effectiveTpslBase}
          mode='new'
          externalQuantity={qty}
          externalOpenPrice={entryPrice ?? null}
          externalSide={side}
          accountCurrency={accountCurrency}
          instrumentCurrency={instrumentInfo?.currency as CurrencyType | undefined}
          onErrorChange={setTpHasInternalError}
        />
      </Box>
      {!isMobile && (
        <Scrollbar
          parentRef={scrollContainerRef}
          isScrollableOverride={isVerticalScrollable}
          orientation='vertical'
          size='sm'
        />
      )}
    </div>
  )
}

interface AddCancellationDateModalProps {
  cancelDate: Date | null
  minCancelDate: Date
  onConfirm: (date: Date) => void
  onClear: () => void
}

const AddCancellationDateModal: BaseModalType<AddCancellationDateModalProps> = (props) => {
  const { cancelDate, minCancelDate, onConfirm, onClear, close } = props
  const { t } = useTranslation()

  const [selectedDate, setSelectedDate] = useState<Date | null>(cancelDate)

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date)
  }

  const handleConfirm = () => {
    if (selectedDate) {
      onConfirm(selectedDate)
      close()
    }
  }

  const handleClear = () => {
    onClear()
    close()
  }

  return (
    <Modal>
      <Modal.Header canClose>{t('Portfolio.Cancellation date')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Calendar
            selected={selectedDate ?? undefined}
            onChange={handleDateChange}
            minDate={minCancelDate}
            monthsShown={1}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={handleClear} disabled={!selectedDate}>
          {t('Clear')}
        </Modal.Footer.Secondary>
        <Modal.Footer.Primary onClick={handleConfirm} disabled={!selectedDate}>
          {t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
